
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { toast } from "sonner";
import { isSharedEntry, removeSharedEntry } from "@/services/sharedNotes.service";
import { EntryCardHeader } from "./EntryCardHeader";
import { EntryCardActions } from "./EntryCardActions";
import { EntryCardBadges } from "./EntryCardBadges";
import { EntryCardFooter } from "./EntryCardFooter";

type Entry = {
  id: string;
  title: string;
  content: string | null;
  entry_type: string;
  creator_id: string;
  creator_email?: string;
  is_favorite: boolean;
  is_pinned?: boolean;
  password?: string | null;
  share_id: string;
  updated_at: string;
  is_shared_entry?: boolean;
  is_postscript?: boolean;
  save_count?: number;
};

type EntryCardProps = {
  entry: Entry;
  currentUserId?: string;
  toggleFavorite: (entry: Entry) => void;
  togglePinned: (entry: Entry) => void;
  copyShareLink: (entry: Entry) => void;
  stripHtmlTags: (html: string | null) => string;
  addToPersonalDashboard?: (entry: Entry) => void;
  onRemove?: (entryId: string) => void;
};

export const EntryCard: React.FC<EntryCardProps> = ({
  entry,
  currentUserId,
  toggleFavorite,
  togglePinned,
  copyShareLink,
  stripHtmlTags,
  addToPersonalDashboard,
  onRemove
}) => {
  const isOwner = currentUserId === entry.creator_id;
  const entryDate = new Date(entry.updated_at).toLocaleDateString();
  const [isSharedEntryState, setIsSharedEntryState] = useState(false);
  
  useEffect(() => {
    if (isOwner && !entry.is_shared_entry) {
      const checkIfShared = async () => {
        const shared = await isSharedEntry(entry.id);
        setIsSharedEntryState(shared);
      };
      
      checkIfShared();
    }
  }, [entry.id, isOwner, entry.is_shared_entry]);
  
  const canShare = !!navigator.share;

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/shared/${entry.share_id}`;
    
    if (canShare) {
      try {
        await navigator.share({
          title: entry.title,
          text: `Check out my note: ${entry.title}`,
          url: shareUrl,
        });
      } catch (err) {
        navigator.clipboard.writeText(shareUrl);
        toast.success("Share link copied to clipboard");
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied to clipboard");
    }
  };
  
  const handleRemoveShared = async () => {
    if (onRemove) {
      onRemove(entry.id);
      return;
    }
    
    const confirmed = window.confirm(
      "Are you sure you want to remove this shared entry from your dashboard?"
    );
    
    if (confirmed) {
      const success = await removeSharedEntry(entry.id);
      if (success) {
        toast.success("Entry removed from your dashboard");
        window.location.reload();
      } else {
        toast.error("Failed to remove entry");
      }
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <EntryCardHeader entry={entry} />
          
          <EntryCardActions
            entry={entry}
            currentUserId={currentUserId}
            isOwner={isOwner}
            isSharedEntryState={isSharedEntryState}
            toggleFavorite={toggleFavorite}
            togglePinned={togglePinned}
            copyShareLink={copyShareLink}
            addToPersonalDashboard={addToPersonalDashboard}
            onRemove={onRemove}
            handleRemoveShared={handleRemoveShared}
          />
        </div>
        
        <EntryCardBadges
          entry={entry}
          isSharedEntryState={isSharedEntryState}
          isOwner={isOwner}
        />
        
        <div className="text-xs text-muted-foreground">
          {entryDate}
          {entry.creator_email && entry.creator_id !== currentUserId && (
            <> · {entry.creator_email}</>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {!entry.password && (
          <p className="text-sm line-clamp-3">
            {stripHtmlTags(entry.content) || "No content"}
          </p>
        )}
        {entry.password && (
          <p className="text-sm italic text-muted-foreground">
            Password protected content
          </p>
        )}
      </CardContent>

      <EntryCardFooter
        entry={entry}
        isOwner={isOwner}
        isSharedEntryState={isSharedEntryState}
        handleShare={handleShare}
        handleRemoveShared={handleRemoveShared}
      />
    </Card>
  );
};
